import { EXPAND_FIELD_PREFIX } from '@constants'
import { CommandParameterDTO, FormRow, ObjectDataRecord } from '@types'

/**
 * Определяет значение по цепочке ссылок, описанной в pathArray.
 *
 * Используемые типы:
 *   @param pathArray      CommandParameterDTO['pathArray'] (EmbeddedObject[])
 *                         Массив шагов обхода от корневого объекта до целевого значения
 *   @param srcObj         ObjectDataRecord — плоский объект с ID-значениями для ref-полей
 *   @param enrichedObject ObjectDataRecord — тот же объект + expands_{field} для ref-полей
 *                         первого уровня
 *   @param fieldsFormConfig FormRow[] — конфиг полей формы; используется для маппинга
 *                         pathArray[0].field → FormRow.code, чтобы корректно обратиться
 *                         к srcObj когда код формы отличается от имени поля в модели
 */
export const resolveByPathArray = (
  pathArray: NonNullable<CommandParameterDTO['pathArray']>,
  srcObj: ObjectDataRecord | null | undefined,
  enrichedObject: ObjectDataRecord | undefined,
  fieldsFormConfig?: FormRow[]
): unknown => {
  if (pathArray.length === 0) return ''

  // Маппинг поля модели на код формы, если они отличаются
  const firstField = pathArray[0].field
  const matchedConfig = fieldsFormConfig?.find(f => f.value === firstField)
  const rootCode = matchedConfig?.code ?? firstField

  // Глубина 1 — значение лежит прямо в srcObj
  if (pathArray.length === 1) {
    return srcObj?.[rootCode] ?? ''
  }

  // enrichedObject из CREATE-формы приходит как {} — не подходит для обхода
  const hasEnrichedData = enrichedObject != null && Object.keys(enrichedObject).length > 0
  const root = (hasEnrichedData ? enrichedObject : srcObj ?? {}) as Record<string, any>
  let current: Record<string, any> = root

  // Обход промежуточных шагов через expands_*
  for (let i = 0; i < pathArray.length - 1; i++) {
    const field = i === 0 ? rootCode : pathArray[i].field
    const expandKey = `${EXPAND_FIELD_PREFIX}${field}`
    const expanded = current?.[expandKey] ?? root?.[expandKey]

    if (expanded != null) {
      current = expanded
    } else {
      // expand отсутствует — возвращаем ID из текущего уровня
      return current?.[field] ?? ''
    }
  }

  // Последний шаг — целевое поле внутри раскрытого объекта
  const lastField = pathArray[pathArray.length - 1].field

  return current?.[lastField] ?? ''
}
